/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import NumberInput from '@ba-ui-toolkit/numberinput';
import FlexLayout, { FlexItem } from '@ba-ui-toolkit/flexlayout';
import Range from './Range';
import styles from './Slider.scss';

const sliderClassName = styles.sliderClassName.replace(/"/g, '');
const rangeSliderFlag = styles.rangeSliderFlag.replace(/"/g, '');
const { sanitize } = propsUtils;
const MAX_NUMBER_DECIMAL = 17;
const getFormatted = (value, scale) => Number(Number(value).toFixed(scale));

export default class RangeSlider extends Component {
	static propTypes = {
		/** Minimum possible value for the slider */
		min: PropTypes.number,
		/** Maximum possible value for the slider */
		max: PropTypes.number,
		/** Function that gets called each time the slider is changed. It provides one parameter, which is the new value of the slider */
		onChange: PropTypes.func,
		/** Function that gets called when user completes input */
		onInputComplete: PropTypes.func,
		/** ID to give to the slider input */
		id: PropTypes.string,
		/** Level of increment for each increase in the slider */
		step: PropTypes.number,
		/** Disables the slider if passed */
		disabled: PropTypes.bool,
		/** Some function that is applied to the numeric values to format their labels for the slider. Should return a string */
		labelFormatter: PropTypes.func,
		/** Option to show the label on the handle */
		showHandleLabel: PropTypes.bool,
		/** Option to show the boundary input box */
		showInputBox: PropTypes.bool,
		/** Option to show the min/max labels */
		showBoundaryLabels: PropTypes.bool,
		/** Flag to display tooltip */
		showTooltip: PropTypes.bool,
		/** Optional size for slider */
		size: PropTypes.oneOf(['small', 'medium']),
		/** ClassName to be applied to the slider container */
		className: PropTypes.string,
		/** Custom CSS styling */
		style: PropTypes.object,
		/** Values in the case of range slider */
		values: PropTypes.arrayOf(PropTypes.number),
		/** Option to invert the range */
		inverted: PropTypes.bool,
		/** Optional, scale for number format */
		scale: PropTypes.number,
	};

	static defaultProps = {
		size: 'medium',
		min: 0,
		max: 100,
		step: 1,
		scale: 3,
		labelFormatter: value => `${value}`,
		className: '',
		style: {},
	};

	state = {
		showFocusClass: false,
	};

	componentWillMount() {
		this._getDerivedStateFromProps(this.props);
	}

	componentWillReceiveProps(nextProps) {
		this._getDerivedStateFromProps(nextProps);
	}

	// The reason why we cannot use React one is that explore remount range slider every time prop change
	_getDerivedStateFromProps(props) {
		const { min, max, scale, values: propValues } = props;
		const values =
			propValues && propValues.length === 2
				? propValues.map(v => getFormatted(v, scale))
				: [min, max];

		this.setState({
			values,
			min: getFormatted(Math.min(values[0], min), scale),
			max: getFormatted(Math.max(values[1], max), scale),
		});
	}

	_setFocus = () => {
		this.setState({ showFocusClass: true });
	};

	_setBlur = () => {
		this.setState({ showFocusClass: false });
	};

	_getDir() {
		const dir = document.querySelector('[dir="rtl"]') ? 'right' : 'left';

		return dir;
	}

	_sliderContainerRef = node => {
		this._sliderContainer = node;
	};

	// numberInput returns undefined with blank input
	_getValidatedValues = values =>
		values.map((v, i) => (isNaN(v) ? this.props.values[i] : Number(v)));

	_onChange = values => {
		const { min, max, onChange, scale } = this.props;
		const validatedValues = this._getValidatedValues(values);

		this.setState({
			min: getFormatted(Math.min(validatedValues[0], min), scale),
			max: getFormatted(Math.max(validatedValues[1], max), scale),
			values: validatedValues.map(v => getFormatted(v, scale)),
		});
		onChange && onChange(validatedValues);
	};

	_toPercentage(value, min, max) {
		return Math.round(((value - min) / (max - min)) * 100);
	}

	_renderHandleLabel(props) {
		const { value, index, min, max, dir, labelFormatter } = props;
		const positionsOnTrackPercent = this._toPercentage(value, min, max);
		const handleLabelStyle = {};
		handleLabelStyle[dir] = `${positionsOnTrackPercent}%`;

		return (
			<p
				key={index}
				style={handleLabelStyle}
				className={`${sliderClassName}__handle_label ${rangeSliderFlag}`}
			>
				{labelFormatter(value)}
			</p>
		);
	}

	render() {
		const {
			id,
			step,
			disabled,
			labelFormatter,
			showHandleLabel,
			showInputBox,
			showBoundaryLabels,
			showTooltip,
			size,
			className,
			style,
			onInputComplete,
			inverted,
			...others
		} = this.props;
		const { min, max, values, showFocusClass } = this.state;

		const containerProps = {
			className: `${sliderClassName} ${className}`,
			style,
		};

		const sliderContainerProps = {
			className: cx(`${sliderClassName}__container`, {
				focus: showFocusClass,
				disabled,
				handleLabelled: showHandleLabel,
				boundaryLabelled: showBoundaryLabels,
			}),
			ref: this._sliderContainerRef,
			style,
		};

		const boundaryLabelProps = {
			className: `${sliderClassName}__boundary_label`,
		};

		const dir = this._getDir();

		const sliderProps = {
			...sanitize(others, RangeSlider),
			className: cx(`${sliderClassName}__container__input`, rangeSliderFlag, inverted, {
				[size]: size,
			}),
			allowCross: false,
			values,
			min,
			max,
			id,
			step,
			disabled,
			showTooltip,
			labelFormatter,
			onChange: this._onChange,
			onInputComplete,
			tabIndex: -1,
			setFocus: this._setFocus,
			setBlur: this._setBlur,
			inverted,
		};

		const renderLeftLabel = () => <div {...boundaryLabelProps}>{labelFormatter(min)}</div>;
		const renderRightLabel = () => <div {...boundaryLabelProps}>{labelFormatter(max)}</div>;

		const renderInputBox = () =>
			showInputBox ? (
				<FlexItem gutter={[0, 0, 5, 0]}>
					<FlexLayout direction="row">
						<FlexItem grow>
							<NumberInput
								scale={MAX_NUMBER_DECIMAL}
								disabled={disabled}
								step={step}
								max={values[1]}
								fullWidth
								value={values[0]}
								onValueAccept={val => this._onChange([val, values[1]])}
							/>
						</FlexItem>
						<FlexItem style={{ width: '1rem' }} />
						<FlexItem grow>
							<NumberInput
								scale={MAX_NUMBER_DECIMAL}
								disabled={disabled}
								step={step}
								min={values[0]}
								fullWidth
								value={values[1]}
								onValueAccept={val => this._onChange([values[0], val])}
							/>
						</FlexItem>
					</FlexLayout>
				</FlexItem>
			) : null;

		const renderBoundaryLabel = () =>
			showInputBox && showBoundaryLabels ? (
				<FlexItem gutter={[1, 0, 0, 0]}>
					<FlexLayout direction="row">
						<FlexItem>{renderLeftLabel()}</FlexItem>
						<FlexItem grow />
						<FlexItem>{renderRightLabel()}</FlexItem>
					</FlexLayout>
				</FlexItem>
			) : null;

		return (
			<FlexLayout direction="column">
				{renderInputBox()}
				<FlexItem style={{ width: '100%' }}>
					<div {...containerProps}>
						{showBoundaryLabels && !showInputBox ? renderLeftLabel() : null}
						<div {...sliderContainerProps}>
							{showHandleLabel && (
								<div
									className={`${sliderClassName}__handle_label_container ${rangeSliderFlag}`}
								>
									{values.map((value, index) =>
										this._renderHandleLabel({
											value,
											index,
											min,
											max,
											dir,
											labelFormatter,
										})
									)}
								</div>
							)}
							<Range {...sliderProps} />
						</div>
						{showBoundaryLabels && !showInputBox ? renderRightLabel() : null}
					</div>
				</FlexItem>
				{renderBoundaryLabel()}
			</FlexLayout>
		);
	}
}
